import { useContext, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import {
  MessageGenerationTable,
  MessageGenerationSessionHeader,
} from '../../components/MessageGeneration';
import {
  MessageGenerationCreateSessionContext,
  MessageGenerationLoadSessionContext,
  MessageGenerationResetSessionContext,
  MessageGenerationStateContext,
  MessageGenerationDispatchContext,
  MessageGenerationTableActionsContext,
} from '../MessageGenSession/Context';

type Props = {
  segmentationSessionId: string;
  segmentId: string;
  // segmentName: string;
};

export const SegmentMessageGeneration = (props: Props) => {
  const session = useContext(MessageGenerationStateContext);
  const dispatch = useContext(MessageGenerationDispatchContext);
  const createMessageGenSession = useContext(MessageGenerationCreateSessionContext);
  const loadSession = useContext(MessageGenerationLoadSessionContext);
  const resetSession = useContext(MessageGenerationResetSessionContext);
  const setTableActions = useContext(MessageGenerationTableActionsContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const apiClient = useChatApiClient();
  const queryClient = useQueryClient();
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);

  const hasExistingSession = !!session;

  useEffect(() => {
    if (!hasExistingSession) {
      console.log('No existing message generation session found, creating a new one...');
      if (!props.segmentationSessionId) {
        console.error('segmentationSessionId is required to create message generation session');
        return;
      }
      if (!brandIdentifier) {
        console.error('brandIdentifier is required to create message generation session');
        return;
      }
      if (!props.segmentId) {
        console.error('segmentId is required to create message generation session');
        return;
      }
      console.log(
        'Creating message generation session with segmentationSessionId:',
        props.segmentationSessionId,
        'segmentId:',
        props.segmentId
      );
      createMessageGenSession({
        sessionId: props.segmentationSessionId,
        brandIdentifier,
        initialState: {
          segmentIds: [props.segmentId],
        },
        pageContext: {
          page: 'segment_profile_dashboard',
          segmentationSessionIdentifier: props.segmentationSessionId,
          segmentIdentifier: props.segmentId,
        },
      });
    }
  }, [
    hasExistingSession,
    createMessageGenSession,
    props.segmentationSessionId,
    props.segmentId,
    brandIdentifier,
  ]);

  const messages = useMemo(() => {
    if (!session) return [];
    return session.state.messages;
  }, [session]);

  const isGenerating = session?.state.phase === 'generating';
  const numberOfMessages = session?.state.numberOfMessages || 5;
  const phase = session?.state.phase || 'idle';
  const isSegment = session?.state.segmentIds && session.state.segmentIds.length === 1;
  const type = isSegment ? 'segment' : 'run';
  const brandName = session?.state.brandName;
  const objective = session?.state.objective;
  const objectiveCategory = session?.state.objectiveCategory;

  // Fetch saved message ids when session loads
  const { data: savedMessageIdentifiers } = useQuery({
    queryKey: ['saved-message-identifiers', session?.id],
    queryFn: () => {
      if (!session?.id) throw new Error('No session ID');
      return apiClient.messageGeneration.getSavedMessageIdentifiers(session.id);
    },
    enabled: !!session?.id,
    staleTime: 30000,
  });

  // Merge saved status into messages
  const messagesWithSavedStatus = useMemo(() => {
    if (!savedMessageIdentifiers || savedMessageIdentifiers.length === 0) return messages;
    return messages.map((msg) => ({
      ...msg,
      saved: savedMessageIdentifiers.includes(msg.identifier),
    }));
  }, [messages, savedMessageIdentifiers]);

  const saveMutation = useMutation({
    mutationFn: async (messageIdentifiers: string[]) => {
      if (!session?.id) throw new Error('No session ID available');
      return apiClient.messageGeneration.saveMessages(session.id, messageIdentifiers);
    },
    onSuccess: (data) => {
      if (session && dispatch && data.savedMessageIdentifiers.length > 0) {
        const updatedMessages = session.state.messages.map((msg) =>
          data.savedMessageIdentifiers.includes(msg.identifier) ? { ...msg, saved: true } : msg
        );
        dispatch({ ...session.state, messages: updatedMessages });
      }
      queryClient.invalidateQueries({ queryKey: ['saved-message-identifiers', session?.id] });
      queryClient.invalidateQueries({ queryKey: ['message-gen-sessions'] });
    },
  });

  const handleSelectSession = (sessionId: number) => {
    if (loadSession) {
      loadSession(sessionId);
    }
  };

  const handleResetSession = async () => {
    await resetSession?.();
  };

  const handleSaveSelected = (messageIdentifiers: string[]) => {
    saveMutation.mutate(messageIdentifiers);
  };

  // Provide table actions to header via context
  useEffect(() => {
    if (setTableActions) {
      setTableActions({
        messages: messagesWithSavedStatus,
        selectionModel,
        sessionId: session?.id,
        phase,
        isSaving: saveMutation.isPending,
        onSaveSelected: handleSaveSelected,
        onResetSession: handleResetSession,
      });
    }
    return () => {
      if (setTableActions) {
        setTableActions(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesWithSavedStatus, selectionModel, session?.id, phase, saveMutation.isPending, setTableActions]);

  if (!session) {
    return null;
  }

  return (
    <Container>
      <MessageGenerationSessionHeader
        sessionId={session.id}
        sessionName=""
        createdOn={session.createdOn}
        phase={phase}
        type={type}
        brandName={brandName}
        objective={objective}
        showPageTitle={true}
        showSessionHistory={true}
        sessionHistoryIconOnly={true}
        onSelectSession={handleSelectSession}
      />
      <MessageGenerationTable
        messages={messagesWithSavedStatus}
        isGenerating={isGenerating}
        sessionId={session.id}
        numberOfMessages={numberOfMessages}
        selectionModel={selectionModel}
        onSelectionModelChange={setSelectionModel}
        phase={phase}
        objectiveCategory={objectiveCategory}
        onSaveSelected={handleSaveSelected}
        isSaving={saveMutation.isPending}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 0;
  border-radius: 12px 12px 0 0;
  overflow: hidden;
`;
